import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Search } from 'lucide-react';
import { Input } from './input';
import { cn } from '../../lib/utils';

/**
 * A single-select dropdown you can narrow by typing.
 *
 * A plain `<select>` is fine for a handful of entries, but fleet lists grow into the
 * hundreds and scrolling one to find a name is the wrong interaction. The input doubles
 * as the search box and as the display of what is currently selected: the typed term
 * lives in the value, and the selected option's label is the placeholder behind it, so
 * the field always says what is picked without a separate label to keep in sync.
 *
 * Options are `{ value, label, description? }`. Matching is case-insensitive over both
 * the value and the label, since a fleet's label carries its device count and the raw
 * name is what people actually type.
 */
export function SearchableSelect({
  options = [],
  value,
  onChange,
  placeholder = 'Search or select…',
  emptyMessage = 'No matches',
  disabled = false,
  id,
  className,
}) {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const filtered = useMemo(() => {
    const needle = term.trim().toLowerCase();
    if (!needle) return options;
    return options.filter(option =>
      String(option.value).toLowerCase().includes(needle) ||
      String(option.label).toLowerCase().includes(needle)
    );
  }, [options, term]);

  const selected = useMemo(
    () => options.find(option => option.value === value) || null,
    [options, value]
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
        setTerm('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const pick = (option) => {
    onChange?.(option.value);
    setOpen(false);
    setTerm('');
    inputRef.current?.blur();
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      setOpen(false);
      setTerm('');
      return;
    }
    // Typing a name and pressing Enter is the fast path this control exists for, so it
    // takes the top match rather than requiring a trip to the mouse.
    if (event.key === 'Enter' && open && filtered.length > 0) {
      event.preventDefault();
      pick(filtered[0]);
    }
  };

  return (
    <div className={cn('relative', className)} ref={containerRef}>
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
      <Input
        ref={inputRef}
        id={id}
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-controls={id ? `${id}-listbox` : undefined}
        autoComplete="off"
        value={term}
        placeholder={selected?.label || placeholder}
        disabled={disabled}
        onFocus={() => setOpen(true)}
        onClick={() => setOpen(true)}
        onChange={(e) => { setTerm(e.target.value); setOpen(true); }}
        onKeyDown={handleKeyDown}
        className={cn(
          'pl-9 pr-8 bg-background',
          // The placeholder is the selected value, so it must read as content, not as a
          // hint — otherwise a picked fleet looks like an empty field.
          selected && !term && 'placeholder:text-foreground'
        )}
      />
      <ChevronDown
        className={cn(
          'absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 transition-transform pointer-events-none',
          open && 'rotate-180'
        )}
      />
      {open && (
        <div
          id={id ? `${id}-listbox` : undefined}
          role="listbox"
          className="absolute z-30 w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-xl max-h-64 overflow-y-auto"
        >
          {filtered.length === 0 ? (
            <p className="px-3 py-2 text-sm text-gray-500">{emptyMessage}</p>
          ) : (
            filtered.map(option => (
              <button
                key={option.value}
                type="button"
                role="option"
                aria-selected={option.value === value}
                // Without this the input blurs before the click lands, which closes the
                // list from under the pointer.
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pick(option)}
                className={cn(
                  'w-full text-left px-3 py-2 hover:bg-slate-50',
                  option.value === value && 'bg-blue-50 border-l-4 border-blue-400'
                )}
              >
                <p className="text-sm font-medium text-gray-900">{option.label}</p>
                {option.description && (
                  <p className="text-xs text-gray-500">{option.description}</p>
                )}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

export default SearchableSelect;
